import {chunk} from '@benchling/js-utils';
import React from 'react';

import {PersistedDataFrameGraphQL} from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import {readableTime} from 'util/DateUtil';
import fuzzydate from 'util/fuzzydate';
import {capitalize, formatBytes} from 'util/NameUtil';

export interface PersistedDataFrameMetadataModalProps {
  persistedDataFrame: PersistedDataFrameGraphQL;
  /**
   * Additional data to show in the grid of metadata. Each entry will get half of a row (or a full row
   * on small screens).
   *
   * @note With current usage, React doesn't seem to mind if each element doesn't have a `key` prop.
   *
   * @example
   * ```
   * [
   *   {
   *     component: (
   *       <FormGroup>
   *         <FieldLabel label="Creator" />
   *         <p>{persistedDataFrame.creator.name}</p>
   *       </FormGroup>
   *     ),
   *     key: 'Creator',
   *   },
   * ]
   * ```
   */
  additionalGridCells?: Array<{component: React.ReactNode; label: string}>;
  /**
   * Additional element to show underneath the grid of metadata.
   *
   * @example <pre>{sql query that created the dataframe}</pre>
   */
  additionalContent?: JSX.Element;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export default function PersistedDataFrameMetadataModal({
  persistedDataFrame,
  additionalGridCells,
  additionalContent,
  onRequestClose,
}: PersistedDataFrameMetadataModalProps): JSX.Element {
  return (
    <Modal
      mediumLarge={true}
      title="Table metadata"
      onRequestClose={onRequestClose}
      dataTestAttrs={{component: 'PersistedDataFrameMetadataModal', element: 'root'}}
    >
      <SharedDataFrameMetadataContents persistedDataFrame={persistedDataFrame} />
      <AdditionalDataFrameMetadataContents
        additionalGridCells={additionalGridCells}
        additionalContent={additionalContent}
      />
    </Modal>
  );
}

function SharedDataFrameMetadataContents({
  persistedDataFrame,
}: Pick<PersistedDataFrameMetadataModalProps, 'persistedDataFrame'>): JSX.Element {
  return (
    <>
      <Row>
        <Col
          sm={6}
          dataTestAttrs={{component: 'PersistedDataFrameMetadataModal', element: 'column', key: 'Name'}}
        >
          <FormGroup>
            <FieldLabel label="Name" />
            <p>{persistedDataFrame.name}</p>
          </FormGroup>
        </Col>
        <Col
          sm={6}
          dataTestAttrs={{component: 'PersistedDataFrameMetadataModal', element: 'column', key: 'Creator'}}
        >
          <FormGroup>
            <FieldLabel label="Creator" />
            <p>{persistedDataFrame.creator.name}</p>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col
          sm={6}
          dataTestAttrs={{
            component: 'PersistedDataFrameMetadataModal',
            element: 'column',
            key: 'Last modified',
          }}
        >
          <FormGroup>
            <FieldLabel label="Last modified" />
            <p>{capitalize(fuzzydate(persistedDataFrame.modifiedAt))}</p>
          </FormGroup>
        </Col>
        <Col
          sm={6}
          dataTestAttrs={{
            component: 'PersistedDataFrameMetadataModal',
            element: 'column',
            key: 'Created at',
          }}
        >
          <FormGroup>
            <FieldLabel label="Created at" />
            <p>{readableTime(persistedDataFrame.createdAt)}</p>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col
          sm={6}
          dataTestAttrs={{
            component: 'PersistedDataFrameMetadataModal',
            element: 'column',
            key: 'Rows',
          }}
        >
          <FormGroup>
            <FieldLabel label="Rows" />
            <p>{persistedDataFrame.numRows}</p>
          </FormGroup>
        </Col>
        <Col
          sm={6}
          dataTestAttrs={{
            component: 'PersistedDataFrameMetadataModal',
            element: 'column',
            key: 'Size',
          }}
        >
          <FormGroup>
            <FieldLabel label="Size" />
            <p>{persistedDataFrame.size ? formatBytes(persistedDataFrame.size) : 'N/A'}</p>
          </FormGroup>
        </Col>
      </Row>
    </>
  );
}

export function AdditionalDataFrameMetadataContents({
  additionalGridCells = [],
  additionalContent,
}: Pick<PersistedDataFrameMetadataModalProps, 'additionalGridCells' | 'additionalContent'>): JSX.Element {
  return (
    <>
      {[...chunk(additionalGridCells, 2)].map((rowCells, rowIndex) => (
        <Row key={rowIndex}>
          {rowCells.map(({label, component}, colIndex) => (
            <Col
              sm={6}
              key={`${label}-${rowIndex}-${colIndex}`}
              dataTestAttrs={{
                component: 'PersistedDataFrameMetadataModal',
                element: 'column',
                key: label,
              }}
            >
              <FormGroup>
                <FieldLabel label={label} />
                <p>{component}</p>
              </FormGroup>
            </Col>
          ))}
        </Row>
      ))}
      <div
        {...getTestProps({
          component: 'PersistedDataFrameMetadataModal',
          element: 'div',
          key: 'additionalContent',
        })}
      >
        {additionalContent}
      </div>
    </>
  );
}
